<template>
    <Modal
        id="create-or-edit-pre-emptive-rights-modal"
        ref="create-or-edit-pre-emptive-rights-modal"
        v-model:open="open"
        :title="
            preselected
                ? 'edit_pre_emptive_rights'
                : 'create_pre_emptive_rights'
        "
        width="xl"
        @cancel="cancel"
    >
        <SelectField
            v-model="modelValue.account_uid"
            :options="accounts"
            name="account_title"
            value="account_uid"
            label="account_title"
            :disabled="preselected"
            :required="true"
            :on-search="loadSelectAccountOptions"
            @change="getQuantityForAccount"
        />

        <NumberField
            v-model="modelValue.quantity"
            :label="$t('quantity')"
            name="quantity"
            :required="true"
            :min="0"
        />

        <template #actions>
            <button
                class="btn bg-primary-accent-500 text-white"
                @click="submit"
            >
                {{ $t('save') }}
            </button>
        </template>
    </Modal>
</template>
<script setup lang="ts">
import { Modal, NumberField } from '@src/components'
import SelectField from '@src/components/SelectField.vue'
import { useVModels } from '@vueuse/core'
import { onMounted, ref, toRefs } from 'vue'
import {
    getPreEmptiveRights,
    PreEmptiveRight,
} from '@utils/api/secondaryMarket'
import { Account, Asset } from '@src/types'
import { getAccount, getAccounts } from '@utils/api/accounts'
import { getAsset } from '@utils/api/assets'

const props = defineProps<{
    open: boolean
    modelValue: PreEmptiveRight | null
    preselected?: boolean
}>()

const { modelValue } = toRefs(props)

const accounts = ref<{ label: string | null; value: number | string | null }[]>(
    []
)

const asset = ref<Asset>(new Asset())

const emit = defineEmits([
    'update:modelValue',
    'update:open',
    'cancel',
    'submit',
])

const { open } = useVModels(props, emit)

const cancel = () => {
    emit('cancel')
}

const submit = () => {
    emit('submit', modelValue.value)
}

onMounted(async () => {
    const assetRequest = await getAsset(modelValue.value?.asset_uid)
    asset.value = assetRequest.data.asset
    if (props.preselected) {
        await loadAccount(modelValue.value.account_uid)
    }
})

async function loadAccount(id: string) {
    const accountRequest = await getAccount(id)
    const account = accountRequest.data.account
    accounts.value = [
        {
            label: account.account_title,
            value: account.account_uid,
        },
    ]
}

async function getQuantityForAccount() {
    if (!modelValue.value?.account_uid) {
        modelValue.value.quantity = 0
        return
    }

    try {
        const searchFilters = {
            filter: {
                account_uid: modelValue.value.account_uid,
                asset_uid: modelValue.value.asset_uid,
            },
        }

        const response = await getPreEmptiveRights()

        const items = response?.data?.data || response?.data || []

        const matchingItem = items.find(
            (item) => item.account_uid === modelValue.value.account_uid
        )

        modelValue.value.quantity = matchingItem ? matchingItem.quantity : 0
    } catch (error) {
        console.error('Failed to fetch pre-emptive rights quantity:', error)
        modelValue.value.quantity = 0
    }
}

async function loadSelectAccountOptions(search: string) {
    const accountsSearchRequest = await getAccounts({
        filter: {
            search: search,
            platform_name_exact: asset.value.platform_name,
        },
    })

    accounts.value = accountsSearchRequest.data.accounts.map(
        (account: Account) => {
            return {
                label: `${account.account_title} (${account.email})`,
                value: account.account_uid as string,
            }
        }
    )
}
</script>
